import React, { useState, useMemo, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { applyBarSoftScale} from "../../helpers/chart/ChartSoftScale";
import { resolveColor} from "../../helpers/chart/ChartColors";
import Select from "~components/Form/components/Select";
import { CleanFiltersIcon } from "~misc/icons";
import { Link, useSearchParams } from "react-router-dom";
import { chartPeriodOptions } from "../../helpers/chart/ChartPeriod";
import useParametros from "~fetchs/useParametros";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, ChartDataLabels);

const buildDatasets = ({ data, setData = () => {}, hidden, conversionData = [],  }) => {
  const order = [];
  const meta = {};
  const volumes = {};
  const keyToGroupId = {};

  data.forEach((month, monthIndex) => {
    Object.values(month.groups).forEach(group => {
      const key = group.slug;

      if (!meta[key]) {
        meta[key] = { label: group.label };
        volumes[key] = Array(data.length).fill(0);
        order.push(key);
      }

      if (group.groupId != null) {
        keyToGroupId[key] = group.groupId;
      }

      volumes[key][monthIndex] = group.volume;
    });
  });

  const series = {};
  order.forEach((key, index) => {
    const gId = keyToGroupId[key] ?? null;
    const legendKey = gId ? `group-${gId}` : key;
    
    if (!series[legendKey]) {
      series[legendKey] = {
        color: resolveColor(key, index, gId, meta[key].label),
        label: meta[key].label,
      };
    }
  });

  let datasets = order
    .filter(key => {
      const gId = keyToGroupId[key] ?? null;
      const legendKey = gId ? `group-${gId}` : key;
      return !hidden[legendKey];
    })
    .map(key => {
      const gId = keyToGroupId[key] ?? null;
      const legendKey = gId ? `group-${gId}` : key;
      return {
        label: meta[key].label,
        backgroundColor: series[legendKey].color,
        data: volumes[key].slice(),
        borderRadius: 2,
        borderWidth: 1,
        borderColor: "#fff",
        stack: "volume",
      };
    });

  const validIndexes = data.map((_, index) =>
    datasets.some(ds => ds.data[index] > 0) ||
    (conversionData[index] != null && conversionData[index] > 0)
  );

  datasets = datasets.map(ds => ({
    ...ds,
    data: ds.data.filter((_, index) => validIndexes[index]),
  }));

  const conversionFiltered = conversionData.filter((_, index) => validIndexes[index]);

  const hasConversion = conversionFiltered.some(v => v > 0);

  if (hasConversion) {
    series['tx-conversao'] = { color: '#00BCD4', label: 'Tx. Conversão', isDashed: true };

    if (!hidden['tx-conversao']) {
      datasets.push({
        type: 'line',
        label: 'Tx. Conversão',
        data: conversionFiltered,
        borderColor: '#00BCD4',
        backgroundColor: 'transparent',
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 3,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#00BCD4',
        pointBorderWidth: 2,
        yAxisID: 'y2',
        order: -1,
        datalabels: {
          anchor: 'end',
          align: 'top',
          offset:-1,
          color: '#B2EBF2',
          font: { size: 10, weight: 'bold' },
          formatter: (value) =>
            value != null && value > 0
              ? `${String(value).replace('.', ',')}%`
              : '',
        },
      });
    }
  }

  const labels = data
    .filter((_, index) => validIndexes[index])
    .map(e => e.label);

  setData?.(series);

  return { labels, datasets: applyBarSoftScale(datasets, data) };
};

const separateChartRegions = {
  id: 'separateNegotiationChartRegions',
  afterLayout(chart) {
    if (!chart.data.datasets.some((dataset) => dataset.type === 'line')) return;

    const { top, bottom } = chart.chartArea;
    const conversionBottom = top + (bottom - top) * 0.3;

    chart.scales.y2.top = top;
    chart.scales.y2.bottom = conversionBottom;
    chart.scales.y2.height = conversionBottom - top;
    chart.scales.y2.configure();

    chart.scales.y.top = conversionBottom + 32;
    chart.scales.y.bottom = bottom;
    chart.scales.y.height = bottom - chart.scales.y.top;
    chart.scales.y.configure();
  },
};

const getChartOptions = (labels, labelSums) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      callbacks: {
        label: ({ dataset, dataIndex, parsed }) => {
          const value = dataset.originalData?.[dataIndex] ?? parsed.y;
          return `${dataset.label}: ${value.toLocaleString("pt-BR")}${dataset.type === 'line' ? '%' : ''}`;
        },
      },
    },
    datalabels: {
      anchor: "center",
      align: "center",
      color: "#fff",
      font: { size: 10, weight: "bold" },
      formatter: (value, ctx) => {
        if (ctx.dataset.type === 'line') return null;
        value = ctx.dataset.originalData[ctx.dataIndex];
        return value != null && value > 0 ? value.toLocaleString("pt-BR") : "";
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: {
        font: { size: 11 },
        color: "#01054C",
        callback: function (_, index) {
          const label = labels[index];
          const total = labelSums[index] ?? 0;
          return [
            label,
            "",
            total ? total.toLocaleString("pt-BR") : "",
            total ? "────" : "",
          ];
        },
      },
    },
    y: {
      stacked: true,
      position: "left",
      grid: { color: "#e5e7eb" },
      ticks: { font: { size: 10 }, color: "#01054C" },
    },
    y2: {
      display: false,
      position: 'right',
      min: -10,
      max: 180,
      grid: { display: false },
    },
  },
  layout: { padding: { top: 10 } },
});

const VolumeInNegotiation = ({
  data = [],
  conversionData = [],
  productGroupOptions = [],
  statusOptions = [],
  initialStatus = 0,
  onFilterChange,
  period = 'default',
  onPeriodChange,
}) => {
  const [hidden, setHidden] = useState({});
  const [series, setSeries] = useState([]);
  const [productGroup, setProductGroup] = useState("");
  const [status, setStatus] = useState(0);
  const [statusNome, setStatusNome] = useState("");
  const [, setSearchParams] = useSearchParams();
  const { getSlug, parameterSlugs } = useParametros();
  const linkRelatorioConcreto = getSlug(parameterSlugs.funil.linkRelatorioConcreto);
  const toggle = (key) => setHidden((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleProductGroup = (opt) => {
    setProductGroup(opt?.value ?? "");
    onFilterChange?.({ productGroup: opt?.value ?? "", status });
  };

  const handleStatus = (opt) => {
    const val = opt?.value ?? 0;
    const name = opt?.label ?? "";

    setStatus(val);
    setStatusNome(name);
    onFilterChange?.({ productGroup, status: val });
  };

  const handleClear = () => {
    onPeriodChange?.('default');
    setProductGroup("");
    setStatus(initialStatus);
    setHidden({});
    onFilterChange?.({ productGroup: "", status: initialStatus });
    setSearchParams((currentParams) => {
      const params = new URLSearchParams(currentParams);
      params.delete('search');
      return params;
    });
  };

  const isStatusMode = !status;

  const chartData = useMemo(() => {
    const { labels, datasets } = buildDatasets({ data, setData: setSeries, hidden, conversionData,  });
    return { labels, datasets };
  }, [data, hidden, conversionData, ]);

  const labelSums = useMemo(() => {
    return chartData.labels.map((_, index) =>
      chartData.datasets.reduce((acc, dataset) => {
        if (dataset.type === "line") return acc;
        return acc + (dataset.originalData[index] || 0);
      }, 0)
    );
  }, [chartData]);

  const chartOptions = useMemo(() => {
    return getChartOptions(chartData.labels, labelSums);
  }, [chartData, labelSums]);

  const Legend = ({ centered }) => (
    <div
      className={
        centered
          ? "flex flex-wrap justify-center items-center gap-x-4 gap-y-2 mb-4"
          : "grid grid-cols-[repeat(3,auto)] justify-start gap-y-4 gap-x-[30px] mt-4 pl-[40px]"
      }
    >
      {Object.entries(series).map(([k, { label, color, isDashed }]) => (
        <button
          key={k}
          type="button"
          onClick={() => toggle(k)}
          className="flex items-center gap-1.5"
          style={{ opacity: hidden[k] ? 0.35 : 1 }}
        >
          {isDashed ? (
            <span
              className="inline-block w-4 h-0 flex-shrink-0"
              style={{
                borderTop: `2px dashed ${color}`,
                position: 'relative',
                top: 1,
              }}
            />
          ) : (
            <span
              className="inline-block w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
          )}
          <span className="text-xs">{label}</span>
        </button>
      ))}
    </div>
  );


  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  return (
    <div className="container mt-8">
      <div className="flex flex-wrap  gap-4 mb-4">
        <div className="w-52">
          <Select
            options={productGroupOptions}
            label="Grupo de Produto:"
            name="productGroup"
            value={productGroupOptions.find((o) => o.value === productGroup) ?? null}
            onChange={handleProductGroup}
            placeholder="Selecione"
            spacingControl={true}
          />
        </div>

        {statusOptions.length > 0 && (
          <div className="w-52">
            <Select
              options={[{ value: 0, label: "Todos" }, ...statusOptions]}
              label="Status"
              name="status"
              value={[{ value: 0, label: "Todos" }, ...statusOptions].find((o) => o.value === status) ?? null}
              onChange={handleStatus}
              placeholder="Selecione"
              spacingControl={true}
            />
          </div>
        )}

        <div className="w-52">
          <Select
            options={chartPeriodOptions}
            label="Período:"
            name="chartPeriod"
            value={chartPeriodOptions.find((option) => option.value === period)}
            onChange={(option) => onPeriodChange?.(option?.value ?? "default")}
            placeholder="Selecione"
            spacingControl={true}
          />
        </div>

        <div className="flex gap-4 items-center ml-auto gap-x-5">
          <div className="w-52 flex justify-center items-center">
            <Link
              type="button"
              target="_blank"
              rel="noopener noreferrer"
              to={linkRelatorioConcreto}
              className="bg-secundaria text-primaria hover:brightness-90 opacity-100 flex justify-between items-center gap-4 p-4 rounded-lg font-bold button-search"
            >
              <span>Relatório de Concreto</span>
            </Link>
          </div>

          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-2 -mb-4 text-sm font-semibold text-primaria"
          >
            <span>Limpar Filtros</span>
            <CleanFiltersIcon />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow px-6 pt-5 pb-4">
        <h3 className="text-center font-bold  text-base mb-4">
          {isStatusMode ? "Volume (t) e Conversão" :  statusNome ? `Volume ${statusNome.toLowerCase()}` : "Volume em negociação"}
        </h3>
        {isStatusMode && <Legend centered />}
        <div style={{ height: chartData.datasets.some((dataset) => dataset.type === "line") ? 400 : 280 }}>
          <Bar data={chartData} options={chartOptions} plugins={[separateChartRegions]} />
        </div>
        {!isStatusMode && <Legend />}
      </div>
    </div>
  );
};

export default VolumeInNegotiation;

